<template>
  <div ref="container" class="progress_switcher" :class="{ mounting: mounting }">
    <div class="progress_switcher__images">
      <div v-for="(item, key) in items" :key="key"
           class="progress_switcher__image"
           :class="{ active: key === active }">
        <picture>
          <img :src="item.image" :alt="item.title">
        </picture>
      </div>
    </div>
    <div class="progress_switcher__items">
      <div v-for="(item, key) in items"
           :key="key"
           class="progress_switcher__item"
           :class="[`progress_switcher__item-${key}`, { active: key === active, animated: key === animated, selected: key === selected }]"
           :style="style(key)">
        <div class="progress_switcher__item-content" @click="onChooseItem(key)">
          <div class="progress_switcher__item-progress">
            <div />
          </div>
          <div class="progress_switcher__item-title" v-html="item.title" />
          <div class="progress_switcher__item-text" v-html="item.text" />
        </div>
        <div class="progress_switcher__item-image">
          <picture>
            <img :src="item.image" :alt="item.title">
          </picture>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProgressSwitcher',
  props: {
    items: Array,
  },
  data () {
    return {
      mounting: true,
      active: 0,
      interval: 0,
      animated: 0,
      selected: null,
      heights: [{
        title: 0,
        total: 0,
      }, {
        title: 0,
        total: 0,
      }, {
        title: 0,
        total: 0,
      }, {
        title: 0,
        total: 0,
      }],
      resizeTimeout: 0,
    }
  },
  methods: {
    style(index) {
      if (this.mounting || this.heights.length === 0) return {};

      return {
        height: `${index === this.active ? this.heights[index].total : this.heights[index].title}px`,
      }
    },
    onChooseItem(index) {
      this.calculateItemHeight(index);
      this.calculateItemHeight(this.active);

      this.selected = index;
      this.active = index;
      this.animated = null;

      clearInterval(this.interval);
    },
    onResize() {
      clearTimeout(this.resizeTimeout);

      this.resizeTimeout = setTimeout(() => {
        this.calculateItemsHeights();
      }, 100);
    },
    startAutoSwitch() {
      this.interval = setInterval(() => {
        const next = this.active + 1 < this.items.length ? this.active + 1 : 0;

        this.calculateItemHeight(next);
        this.calculateItemHeight(this.active);

        this.active = next;
        this.animated = next;
      }, 8000);
    },
    getItemHeights(item) {
      const title = item.querySelector('.progress_switcher__item-title');
      const text = item.querySelector('.progress_switcher__item-text');
      const image = item.querySelector('.progress_switcher__item-image');

      const titleRect = title.getBoundingClientRect();
      const textRect = text.getBoundingClientRect();
      const imageRect = image.getBoundingClientRect();

      return {
        title: titleRect.height,
        total: titleRect.height + textRect.height + 10 + imageRect.height,
      };
    },
    calculateItemHeight(index) {
      if (this.mounting) return;

      const item = this.$refs.container.querySelector(`.progress_switcher__item-${index}`);

      this.heights[index] = this.getItemHeights(item);
    },
    calculateItemsHeights() {
      return new Promise((resolve) => {
        const container = this.$refs.container.querySelector('.progress_switcher__items');
        const clone = container.cloneNode(true);
        const rect = container.getBoundingClientRect();

        clone.style.position = 'absolute';
        clone.style.width = `${rect.width}px`;
        clone.style.background = '#fff';
        clone.style.opacity = '0';

        this.$refs.container.appendChild(clone);

        const items = Array.from(clone.querySelectorAll('.progress_switcher__item'));

        items.forEach((item) => {
          const title = item.querySelector('.progress_switcher__item-title');
          const text = item.querySelector('.progress_switcher__item-text');

          title.style.display = 'block';
          text.style.display = 'block';
        });

        setTimeout(() => {
          items.forEach((item, index) => {
            this.heights[index] = this.getItemHeights(item);
          });

          clone.remove();

          resolve();
        }, 16);
      });
    },
    progressiveImageLoadings() {
      const promises = [];

      this.items.forEach((item) => {
        promises.push(new Promise((resolve) => {
          const image = new Image();

          image.onload = () => {
            image.remove();

            resolve();
          }

          image.src = item.image;
        }))
      });

      return Promise.all(promises);
    }
  },
  mounted() {
    this.startAutoSwitch();

    this.progressiveImageLoadings().then(() => {
      this.calculateItemsHeights().then(() => {
        this.mounting = false;
      });
    });

    window.addEventListener('resize', this.onResize);
  },
  beforeMount() {
    window.removeEventListener('resize', this.onResize);
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/breakpoints";
@import "@/styles/button-primary.scss";

.progress_switcher {
  display: flex;
  gap: 44px;

  &.mounting {
    .progress_switcher__items {
      .progress_switcher__item {
        &.active {
          .progress_switcher__item-text {
            display: block;
          }
        }

        .progress_switcher__item-text {
          display: none;
        }
      }
    }
  }

  .progress_switcher__images {
    position: relative;
    width: 53%;
    flex: 1 0 auto;
    margin: -18px -1px 0;

    .progress_switcher__image {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;

      &.active {
        opacity: 1;
        visibility: visible;
      }
    }

    img {
      width: 100%;
    }
  }

  .progress_switcher__items {
    display: flex;
    flex-direction: column;
    gap: 30px;

    .progress_switcher__item {
      position: relative;
      overflow: hidden;
      transition: height 0.3s ease-in-out;

      &.active {
        .progress_switcher__item-title {
          color: #333334;
        }
      }

      &.animated {
        .progress_switcher__item-progress {
          div {
            animation: progress 8s linear;
          }
        }
      }

      &.selected {
        .progress_switcher__item-progress {
          div {
            height: 100%;
          }
        }
      }

      .progress_switcher__item-content {
        cursor: pointer;
        position: relative;
        padding: 0 0 0 18px;
      }

      .progress_switcher__item-progress {
        position: absolute;
        left: 0;
        top: 0;
        width: 3px;
        height: 100%;
        background-color: #E9E9E9;
        border-radius: 3px;
        overflow: hidden;

        div {
          position: absolute;
          left: 0;
          top: 0;
          width: 3px;
          background-color: #3699FF;
          border-radius: 3px;
        }
      }

      .progress_switcher__item-title {
        font-size: 20px;
        font-weight: 600;
        line-height: 28px;
        color: #757981;
        transition: color 0.3s ease;
      }

      .progress_switcher__item-text {
        font-size: 18px;
        line-height: 28px;
        margin: 10px 0 0;
        overflow: hidden;
      }

      .progress_switcher__item-image {
        display: none;

        img {
          width: 100%;
        }
      }
    }
  }
}

@include media-breakpoint-down($size-tablet) {
  .progress_switcher {
    .progress_switcher__images {
      display: none;
    }

    &.mounting {
      .progress_switcher__items {
        .progress_switcher__item {
          &.active {
            .progress_switcher__item {
              display: block;
            }
          }

          .progress_switcher__item-text {
            display: none;
          }
        }
      }
    }

    .progress_switcher__items {
      .progress_switcher__item {
        //&.active {
        //  .progress_switcher__item-image {
        //    display: block;
        //  }
        //}

        .progress_switcher__item-image {
          display: block;
          margin: 0 -15px;
          padding: 22px 0 9px;
        }
      }
    }
  }
}

@include media-breakpoint-down($size-mobile) {
  .progress_switcher {
    .progress_switcher__items {
      gap: 22px;

      .progress_switcher__item {
        .progress_switcher__item-title {
          font-size: 16px;
          line-height: 24px;
        }

        .progress_switcher__item-text {
          font-size: 15px;
          line-height: 24px;
        }

        .progress_switcher__item-image {
          margin: 0;
          padding: 16px 0 8px;
        }
      }
    }
  }
}

@keyframes progress {
  0% {
    height: 0;
  }
  100% {
    height: 100%;
  }
}
</style>
